import { useState, useRef, useMemo, useEffect } from "react"
import arrayUtilities from "../../utilities/arrayUtilities"
import stringHelpers from "../../utilities/stringHelpers"

export const AutoComplete = ({ options = [], selected, onSelect, onSearch = () => {}, placeholder = "Type or select...", width, isDisabled = false }) => {
    const [inputValue, setInputValue] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [isFiltering, setIsFiltering] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const suppressBlurRef = useRef(false)
    const itemRefs = useRef([])
    const debounceRef = useRef(null)

    useEffect(() => {
        const label = options.find(option => option.value === selected)?.label ?? selected ?? ''
        setInputValue(label)
    }, [selected, options])

    const filteredOptions = useMemo(() => {
        if (!isFiltering || stringHelpers.isNullOrEmpty(inputValue)) return options
        return options.filter(option =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
    }, [inputValue, options, isFiltering])

    useEffect(() => {
        setHighlightedIndex(-1)
    }, [filteredOptions])

    useEffect(() => {
        if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
            itemRefs.current[highlightedIndex].scrollIntoView({ block: 'nearest' })
        }
    }, [highlightedIndex])

    useEffect(() => {
        return () => clearTimeout(debounceRef.current)
    }, [])

    const handleSelect = (option) => {
        clearTimeout(debounceRef.current)
        const value = option.value.trim()
        setInputValue(value)
        setIsOpen(false)
        setHighlightedIndex(-1)
        onSelect(value)
        onSearch(value)
    }

    const handleChange = (event) => {
        const value = event.target.value
        setInputValue(value)
        setIsOpen(true)
        setIsFiltering(true)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => onSearch(value.trim()), 500)
    }

    const handleFocus = () => {
        setIsOpen(true)
        setIsFiltering(false)
    }

    const handleBlur = () => {
        if (suppressBlurRef.current) return
        clearTimeout(debounceRef.current)
        setIsOpen(false)
        setHighlightedIndex(-1)
        onSelect(inputValue.trim())
    }

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setIsOpen(true)
            setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1))
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlightedIndex(prev => Math.max(prev - 1, 0))
        } else if (event.key === 'Enter') {
            if (highlightedIndex >= 0) {
                handleSelect(filteredOptions[highlightedIndex])
            } else {
                clearTimeout(debounceRef.current)
                setIsOpen(false)
                const value = inputValue.trim()
                onSelect(value)
                onSearch(value)
            }
        } else if (event.key === 'Escape') {
            setIsOpen(false)
            setHighlightedIndex(-1)
        }
    }

    const handleDropdownMouseDown = () => {
        suppressBlurRef.current = true
        setTimeout(() => { suppressBlurRef.current = false }, 0)
    }

    return (
        <div
            className="border rounded position-relative auto-complete"
            style={{ width: width !== undefined ? width : '100%' }}
        >
            <input
                type="text"
                disabled={isDisabled}
                value={inputValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="form-control border-0 rounded"
            />
            {isOpen && !arrayUtilities.isNullOrEmpty(filteredOptions) && (
                <div
                    className="px-0 border rounded bg-white position-absolute top-100 left-0 search-select-dropdown"
                    onMouseDown={handleDropdownMouseDown}
                >
                    <ul className="pl-0 w-100 mb-2 mt-1">
                        {filteredOptions.map((option, index) => (
                            <li
                                key={option.value}
                                ref={el => itemRefs.current[index] = el}
                                onClick={() => handleSelect(option)}
                                className={`dropdown-item cursor-pointer my-1 ml-0 pl-2 fs--1${highlightedIndex === index ? ' highlighted' : ''}`}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
